import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getPhoneDigits, isValidUsPhoneNumber } from "../lib/phone";
import { AUTH_STORAGE_KEY, IS_TEST_MODE, ROUTES, SESSION_TOKEN_KEY } from "../constants";
import { authHeaders } from "../lib/api-client";
import { getReferralSource } from "../lib/referral";

export type UserRole = "user" | "admin";

export interface AuthState {
  username: string;
  email: string;
  role: UserRole;
  phoneNumber: string;
}

interface AuthContextValue {
  auth: AuthState | null;
  authLoading: boolean;
  login: (
    username: string,
    password: string
  ) => Promise<{ ok: true; role: UserRole } | { ok: false; error: string }>;
  signup: (
    username: string,
    password: string,
    extra?: { phoneNumber?: string; email?: string; verificationResumeToken?: string }
  ) => Promise<{ ok: true; message: string } | { ok: false; error: string }>;
  verifyAccount: (
    code: string
  ) => Promise<{ ok: true; role: UserRole } | { ok: false; error: string }>;
  updateProfile: (input: {
    email: string;
    phoneNumber: string;
  }) => Promise<{ ok: true; email: string } | { ok: false; error: string }>;
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  forgotPassword: (identifier: string) => Promise<{ ok: boolean; error?: string }>;
  resetPassword: (code: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
}

export function persistAuthState(authState: AuthState | null) {
  if (typeof window === "undefined") {
    return;
  }
  if (authState) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authState));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

// In test mode, auth is fully localStorage-backed with static demo users so tests
// remain synchronous and require no network mocking.
const IS_TEST = IS_TEST_MODE;

type TestUser = {
  username: string;
  email: string;
  password: string;
  role: UserRole;
  phoneNumber: string;
};
const TEST_USERS: TestUser[] = [
  {
    username: "pilot",
    email: "pilot@constructorfleet.com",
    password: "user123",
    role: "user",
    phoneNumber: "555-123-4567",
  },
  {
    username: "admin",
    email: "admin@constructorfleet.com",
    password: "admin123",
    role: "admin",
    phoneNumber: "555-123-0001",
  },
];

function loadTestAuth(): AuthState | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (
      (parsed.role === "user" || parsed.role === "admin") &&
      typeof parsed.username === "string"
    ) {
      return {
        username: parsed.username,
        email: typeof parsed.email === "string" ? parsed.email : "",
        role: parsed.role,
        phoneNumber: typeof parsed.phoneNumber === "string" ? parsed.phoneNumber : "",
      };
    }
  } catch {
    // ignore malformed values
  }
  localStorage.removeItem(AUTH_STORAGE_KEY);
  return null;
}

async function apiFetch(path: string, options?: RequestInit) {
  return fetch(`/api/auth${path}`, {
    ...options,
    headers: authHeaders(options?.headers),
  });
}

type ApiUser = {
  username: string;
  email: string | null;
  role: "admin" | "user";
  phoneNumber: string;
};
type LoginResponse = { ok: boolean; error?: string; user?: ApiUser; sessionToken?: string };
type SignupResponse = { ok: boolean; error?: string; message?: string };
type VerifyResponse = { ok: boolean; error?: string; user?: ApiUser; sessionToken?: string };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Provides authentication state and auth actions for the application.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState | null>(() => (IS_TEST ? loadTestAuth() : null));
  const [authLoading, setAuthLoading] = useState(() => {
    if (IS_TEST) return false;
    return Boolean(localStorage.getItem(SESSION_TOKEN_KEY));
  });

  const refreshSession = () => {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) return;

    apiFetch("/session", { method: "GET" })
      .then(async (res) => {
        if (!res.ok) {
          localStorage.removeItem(SESSION_TOKEN_KEY);
          return;
        }
        const data = (await res.json()) as { ok: boolean; user?: ApiUser };
        if (data.ok && data.user) {
          setAuth({
            username: data.user.username,
            email: data.user.email ?? "",
            role: data.user.role,
            phoneNumber: data.user.phoneNumber,
          });
        } else {
          localStorage.removeItem(SESSION_TOKEN_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(SESSION_TOKEN_KEY);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  };

  useEffect(() => {
    if (IS_TEST) return;
    refreshSession();
  }, []);

  useEffect(() => {
    if (IS_TEST) return;
    const handler = () => refreshSession();
    window.addEventListener("cf:auth-changed", handler);
    return () => window.removeEventListener("cf:auth-changed", handler);
  }, []);

  useEffect(() => {
    persistAuthState(auth);
  }, [auth]);

  const value = useMemo<AuthContextValue>(
    () => ({
      auth,
      authLoading,

      login: async (usernameInput, passwordInput) => {
        const username = usernameInput.trim().toLowerCase();

        if (IS_TEST) {
          const matched = TEST_USERS.find(
            (u) => u.username === username && u.password === passwordInput
          );
          if (!matched) return { ok: false, error: "Invalid username or password." };
          const nextAuth: AuthState = {
            username: matched.username,
            email: matched.email,
            role: matched.role,
            phoneNumber: matched.phoneNumber,
          };
          setAuth(nextAuth);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
          return { ok: true, role: matched.role };
        }

        try {
          const res = await apiFetch("/login", {
            method: "POST",
            body: JSON.stringify({ username, password: passwordInput }),
          });
          const data = (await res.json()) as LoginResponse;
          if (!data.ok || !data.user || !data.sessionToken) {
            return { ok: false, error: data.error ?? "Login failed." };
          }
          localStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
          const nextAuth: AuthState = {
            username: data.user.username,
            email: data.user.email ?? "",
            role: data.user.role,
            phoneNumber: data.user.phoneNumber,
          };
          setAuth(nextAuth);
          return { ok: true, role: nextAuth.role };
        } catch {
          return { ok: false, error: "Unable to connect. Please try again." };
        }
      },

      signup: async (usernameInput, passwordInput, extra) => {
        const username = usernameInput.trim().toLowerCase();
        const email = extra?.email?.trim().toLowerCase() ?? "";

        if (IS_TEST) {
          if (!/^[a-z][a-z0-9_]{2,31}$/.test(username)) {
            return { ok: false, error: "Enter a valid username." };
          }
          if (email && !email.includes("@"))
            return { ok: false, error: "Enter a valid email address." };
          if (passwordInput.length < 8)
            return { ok: false, error: "Password must be at least 8 characters." };
          if (TEST_USERS.some((u) => u.username === username)) {
            return { ok: false, error: "An account with that username already exists." };
          }
          const nextAuth: AuthState = {
            username,
            email,
            role: "user",
            phoneNumber: extra?.phoneNumber ?? "",
          };
          setAuth(nextAuth);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
          return { ok: true, message: "Test account created. Enter any code below to verify." };
        }

        try {
          const res = await apiFetch("/signup", {
            method: "POST",
            body: JSON.stringify({
              username,
              email,
              password: passwordInput,
              phoneNumber: extra?.phoneNumber,
              verificationResumeToken: extra?.verificationResumeToken,
              referralSource: getReferralSource() ?? undefined,
            }),
          });
          const data = (await res.json()) as SignupResponse;
          if (!data.ok) {
            return { ok: false, error: data.error ?? "Signup failed." };
          }
          return {
            ok: true,
            message:
              data.message ??
              "A verification code was sent. Enter it below to finish setting up your account.",
          };
        } catch {
          return { ok: false, error: "Unable to connect. Please try again." };
        }
      },

      verifyAccount: async (code) => {
        if (IS_TEST) {
          const [first] = TEST_USERS;
          const nextAuth: AuthState = {
            username: first.username,
            email: first.email,
            role: first.role,
            phoneNumber: first.phoneNumber,
          };
          setAuth(nextAuth);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
          return { ok: true, role: first.role };
        }

        try {
          const res = await apiFetch("/verify", {
            method: "POST",
            body: JSON.stringify({ code }),
          });
          const data = (await res.json()) as VerifyResponse;
          if (!data.ok || !data.user || !data.sessionToken) {
            return { ok: false, error: data.error ?? "Verification failed." };
          }
          localStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
          const nextAuth: AuthState = {
            username: data.user.username,
            email: data.user.email ?? "",
            role: data.user.role,
            phoneNumber: data.user.phoneNumber,
          };
          setAuth(nextAuth);
          return { ok: true, role: nextAuth.role };
        } catch {
          return { ok: false, error: "Unable to verify your account. Please try again." };
        }
      },

      updateProfile: async ({ email: nextEmailInput, phoneNumber: nextPhoneInput }) => {
        if (!auth) return { ok: false, error: "No active user session." };

        const nextEmail = nextEmailInput.trim().toLowerCase();
        const nextPhone = nextPhoneInput.trim();
        const hasPhone = nextPhone.length > 0;

        if (nextEmail && !nextEmail.includes("@"))
          return { ok: false, error: "Enter a valid email address." };
        if (hasPhone && !isValidUsPhoneNumber(nextPhone).ok) {
          return { ok: false, error: "Mobile phone number must be a valid 10-digit US number." };
        }

        if (!IS_TEST) {
          try {
            const res = await apiFetch("/profile", {
              method: "POST",
              body: JSON.stringify({
                email: nextEmail,
                phoneNumber: nextPhone,
              }),
            });
            const data = (await res.json()) as {
              ok: boolean;
              error?: string;
              user?: { email: string | null; phoneNumber: string };
            };
            if (!res.ok || !data.ok || !data.user) {
              return { ok: false as const, error: data.error ?? "Unable to update profile." };
            }
            const nextAuth: AuthState = {
              ...auth,
              email: data.user.email ?? "",
              phoneNumber: data.user.phoneNumber,
            };
            setAuth(nextAuth);
            return { ok: true as const, email: nextAuth.email };
          } catch {
            return { ok: false as const, error: "Unable to connect. Please try again." };
          }
        }

        const nextAuth: AuthState = {
          ...auth,
          email: nextEmail,
          phoneNumber: getPhoneDigits(nextPhone),
        };
        setAuth(nextAuth);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
        return { ok: true, email: nextEmail };
      },

      changePassword: async (currentPassword, newPassword) => {
        if (!auth) return { ok: false, error: "No active user session." };
        if (IS_TEST) return { ok: true };
        try {
          const res = await apiFetch("/change-password", {
            method: "POST",
            body: JSON.stringify({
              currentPassword,
              newPassword,
            }),
          });
          const data = (await res.json()) as { ok: boolean; error?: string };
          return data;
        } catch {
          return { ok: false, error: "Unable to connect. Please try again." };
        }
      },

      logout: () => {
        if (!IS_TEST) {
          const token = localStorage.getItem(SESSION_TOKEN_KEY);
          if (token) {
            apiFetch("/logout", { method: "POST", keepalive: true }).catch(() => {});
            localStorage.removeItem(SESSION_TOKEN_KEY);
            window.location.href = `${ROUTES.LOGIN}?logged_out=true`;
          }
        }
        persistAuthState(null);
        setAuth(null);
      },

      forgotPassword: async (identifier) => {
        if (IS_TEST) return { ok: true };
        try {
          const res = await apiFetch("/forgot-password", {
            method: "POST",
            body: JSON.stringify({ identifier }),
          });
          const data = (await res.json()) as { ok: boolean; error?: string };
          return data;
        } catch {
          return { ok: false, error: "Unable to connect. Please try again." };
        }
      },

      resetPassword: async (code, newPassword) => {
        if (IS_TEST) return { ok: true };
        try {
          const res = await apiFetch("/reset-password", {
            method: "POST",
            body: JSON.stringify({ code, newPassword }),
          });
          const data = (await res.json()) as { ok: boolean; error?: string };
          return data;
        } catch {
          return { ok: false, error: "Unable to connect. Please try again." };
        }
      },
    }),
    [auth, authLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Returns the active authentication context for the current app session.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
